// Catalogue des ÉVÉNEMENTS (docs/specs-fonctionnelles.md §7).
// Chaque action de l'utilisateur devient un événement, validé par un schéma zod
// des deux côtés (navigateur avant envoi, serveur à la réception : R-OFF-07).
import { z } from 'zod';

const iso = z.string().min(10); // date-heure ISO 8601, ex. 2026-10-06T19:42:00.000Z
const id = z.string().min(1).max(200);
const domaine = z.enum(['AG', 'EL', 'PR', 'LA', 'IA', 'CA']);
const colonne = z.enum(['a_faire', 'en_cours', 'fait']);

/** Champs communs à tous les événements. */
const base = {
  id: z.string().uuid(),
  cree_le: iso,
  appareil: z.string().min(1).max(100),
  version_schema: z.literal(1),
};

const ev = <T extends string, C extends z.ZodTypeAny>(type: T, charge: C) =>
  z.object({ ...base, type: z.literal(type), charge });

export const schemaEvenement = z.discriminatedUnion('type', [
  ev('saison_activee', z.object({
    saison: z.number().int().min(1).max(4),
    debut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    mode_agile: z.enum(['kanban', 'scrum']),
    livrable: z.object({ titre: z.string(), description: z.string().default(''), jalons: z.array(z.string()).default([]) }).optional(),
  })),
  ev('session_commencee', z.object({ session: id, mode: z.enum(['standard', 'faible_energie']), titre_exercice: z.string().optional() })),
  ev('etape_terminee', z.object({ session: id, index: z.number().int().min(0) })),
  ev('etape_reouverte', z.object({ session: id, index: z.number().int().min(0) })),
  ev('capsule_lue', z.object({ session: id, capsule: id })),
  ev('session_terminee', z.object({
    session: id,
    mode: z.literal('standard'),
    /** Indicatif, jamais bloquant : une session peut rester ouverte plusieurs soirs (voir R-SES-04). */
    duree_min: z.number().int().min(0).optional(),
    energie: z.number().int().min(1).max(5).optional(),
    texte: z.string().min(10).max(5000),
    commit_fait: z.boolean().default(false),
    /** Identifiants des cartes de la session, à activer (R-CAR-02). Rempli par le client depuis le contenu. */
    cartes: z.array(id).default([]),
    entree_journal: z.string().uuid(),
  })),
  ev('session_faible_energie_terminee', z.object({ session: id, texte: z.string().min(5).max(5000), entree_journal: z.string().uuid() })),
  ev('session_abandonnee', z.object({ session: id })),
  ev('blocage_declenche', z.object({ exercice: id, session: id })),
  ev('palier_consulte', z.object({ exercice: id, session: id, palier: z.number().int().min(1).max(4) })),
  ev('abandon_exercice', z.object({ exercice: id, session: id, prochaine_session: id.optional() })),
  ev('variante_terminee', z.object({ exercice: id, session: id })),
  ev('carte_revisee', z.object({ carte: id, note: z.union([z.literal(1), z.literal(3), z.literal(4), z.literal(5)]) })),
  ev('carte_creee', z.object({ carte: id, recto: z.string().min(1).max(2000), verso: z.string().min(1).max(5000), domaine: domaine.optional() })),
  ev('carte_suspendue', z.object({ carte: id })),
  ev('carte_reactivee', z.object({ carte: id })),
  ev('entree_journal_ajoutee', z.object({
    entree: z.string().uuid(),
    type: z.enum(['cloture', 'libre', 'revue_sprint', 'retro_sprint', 'bilan_saison']),
    texte: z.string().min(1).max(20000),
    session: id.optional(),
    sprint: id.optional(),
    energie: z.number().int().min(1).max(5).optional(),
  })),
  ev('entree_journal_modifiee', z.object({ entree: z.string().uuid(), texte: z.string().min(1).max(20000) })),
  ev('entree_journal_archivee', z.object({ entree: z.string().uuid() })),
  ev('note_mentor_ajoutee', z.object({
    entree: z.string().uuid(),
    sous_type: z.enum(['review', 'defi', 'soutenance', 'bilan', 'libre']),
    texte: z.string().min(1).max(20000),
    session: id.optional(),
  })),
  ev('element_cree', z.object({
    element: id,
    titre: z.string().min(1).max(300),
    description: z.string().max(5000).optional(),
    domaine: domaine.optional(),
    colonne: colonne.default('a_faire'),
    source: z.enum(['exercice', 'livrable', 'jalon', 'manuel']).default('manuel'),
    session: id.optional(),
    story: z.object({ en_tant_que: z.string(), je_veux: z.string(), afin_de: z.string() }).optional(),
    criteres: z.array(z.string()).optional(),
  })),
  ev('element_modifie', z.object({
    element: id,
    titre: z.string().min(1).max(300).optional(),
    description: z.string().max(5000).optional(),
    domaine: domaine.optional(),
    story: z.object({ en_tant_que: z.string(), je_veux: z.string(), afin_de: z.string() }).optional(),
    criteres: z.array(z.object({ texte: z.string(), fait: z.boolean() })).optional(),
  })),
  ev('element_deplace', z.object({ element: id, colonne, position: z.number().int().min(0) })),
  ev('element_supprime', z.object({ element: id })),
  ev('sprint_cree', z.object({ sprint: id, numero: z.number().int().min(1), objectif: z.string().max(1000), debut: z.string(), fin: z.string() })),
  ev('sprint_demarre', z.object({ sprint: id })),
  ev('sprint_termine', z.object({ sprint: id })),
  ev('element_engage', z.object({ sprint: id, element: id, engage: z.boolean() })),
  ev('defi_marque', z.object({ defi: id, statut: z.enum(['propose', 'fait', 'passe']) })),
  ev('soutenance_marquee', z.object({ soutenance: id, statut: z.enum(['a_faire', 'faite']) })),
  ev('projet_cree', z.object({ projet: id, titre: z.string().min(1).max(300), description: z.string().max(5000).default(''), saison: z.number().int().min(1).max(4), depot: z.string().url().optional(), demo: z.string().url().optional() })),
  ev('projet_modifie', z.object({ projet: id, titre: z.string().min(1).max(300).optional(), description: z.string().max(5000).optional(), statut: z.enum(['en_cours', 'livre']).optional(), depot: z.string().url().or(z.literal('')).optional(), demo: z.string().url().or(z.literal('')).optional() })),
  /** Remplit le bloc étude de cas d'un projet (R-PTF-05) ; les champs absents ne changent pas. */
  ev('etude_cas_modifiee', z.object({
    projet: id,
    readme: z.string().max(50000).optional(),
    script_demo: z.string().max(20000).optional(),
    post: z.string().max(5000).optional(),
    statut: z.enum(['brouillon', 'pret']).optional(),
    checklist: z.object({
      secrets: z.boolean().optional(),
      donnees: z.boolean().optional(),
      infra: z.boolean().optional(),
      relu: z.boolean().optional(),
    }).optional(),
  })),
  /**
   * Passe l'étude de cas en « publiée ». Le réducteur REFUSE tant que la checklist n'est pas
   * complète (R-PTF-06) : le schéma ne peut pas le vérifier, l'état si.
   */
  ev('etude_cas_publiee', z.object({ projet: id })),
  /** Une entrée mensuelle de veille marché (R-VEI-01). */
  ev('veille_ajoutee', z.object({
    entree: z.string().uuid(),
    offres: z.string().min(1).max(100000),
    competences: z.array(z.string().min(1).max(200)).max(100).default([]),
  })),
  /** Une ligne de la carte des écarts (R-VEI-02). */
  ev('ecart_marque', z.object({
    competence: z.string().min(1).max(200),
    couverture: z.enum(['couverte', 'partielle', 'absente']),
    saison: z.number().int().min(1).max(4).optional(),
  })),
  ev('capture_ajoutee', z.object({ projet: id, fichier: id })),
  ev('capture_retiree', z.object({ projet: id, fichier: id })),
  ev('parametres_modifies', z.object({
    jours: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
    vendredi: z.boolean().optional(),
    debut_saisons: z.record(z.string(), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
    faible_energie_defaut: z.boolean().optional(),
    theme: z.enum(['auto', 'clair', 'sombre']).optional(),
  })),
  ev('semaine_off_marquee', z.object({ semaine: z.string().regex(/^\d{4}-W\d{2}$/) })),
  ev('semaine_off_retiree', z.object({ semaine: z.string().regex(/^\d{4}-W\d{2}$/) })),
  ev('saison_suivante', z.object({ saison: z.number().int().min(1).max(4) })),
  /** Lance un parcours annexe (R-PAR-02) ; son livrable rejoint le portfolio et le tableau (R-PAR-05). */
  ev('parcours_active', z.object({
    parcours: z.string().regex(/^[a-z][a-z0-9-]*$/),
    livrable: z.object({ titre: z.string(), description: z.string().default(''), jalons: z.array(z.string()).default([]) }).optional(),
  })),
]);

export type Evenement = z.infer<typeof schemaEvenement>;
export type TypeEvenement = Evenement['type'];

/** Un événement tel que le serveur le renvoie : numéroté dans l'ordre de réception. */
export type EvenementSequence = Evenement & { sequence: number; recu_le: string };

/** Ce que le navigateur envoie à POST /api/sync (docs/architecture.md §4.4). */
export const schemaRequeteSync = z.object({
  appareil: z.string().min(1).max(100),
  depuis_sequence: z.number().int().min(0),
  evenements: z.array(z.unknown()).max(500),
});

export interface ReponseSync {
  acquittes: string[];
  rejetes: { id: string; raison: string }[];
  nouveaux: EvenementSequence[];
  sequence: number;
}

/** Valide un événement inconnu ; renvoie l'événement typé ou un message d'erreur en français. */
export function validerEvenement(brut: unknown): { ok: true; evenement: Evenement } | { ok: false; raison: string } {
  const resultat = schemaEvenement.safeParse(brut);
  if (resultat.success) return { ok: true, evenement: resultat.data };
  const premiere = resultat.error.issues[0];
  const chemin = premiere?.path.join('.') || 'événement';
  return { ok: false, raison: `${chemin} : ${premiere?.message ?? 'invalide'}` };
}
